/*
 * TallyMetrics — primitive tally catalog (auto-generated per workspace
 * from synced tracker metadata) plus a quick view of computed bucket
 * counts so analysts can see what's tallied and how.
 *
 * Reused for both consoles. Workspace scope shows only the user's own
 * tallies; platform scope shows everything (and could later filter by
 * the WorkspaceSelector — left as Phase 6 follow-up).
 *
 * Recompute action enqueues a tally-recompute job so analysts can
 * rebucket existing tracker events after a metadata change without
 * waiting for the next scheduled sync.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import WorkspaceSelector from "../layouts/WorkspaceSelector.js";
import { api, ApiError } from "../lib/api.js";
import { NamedId, useLabels } from "../lib/labels.js";
import { SkeletonTable } from "../components/Skeleton.js";
import LivePanel from "../components/LivePanel.js";
import { useLiveEvents } from "../lib/useLiveEvents.js";

type Tally = Awaited<ReturnType<typeof api.listTallies>>["tallies"][number];
type Bucket = Awaited<ReturnType<typeof api.listBuckets>>["buckets"][number];

// Professional, intent-revealing labels derived from the tally shape.
// "Presence" was confusing — analysts read it as "a status" rather than
// "count of records with a value", so we surface clearer wording. The
// kind column reads top-to-bottom: what is being counted / measured.
const KIND_FILTER_LABELS: Record<string, string> = {
  "event-count": "Event count",
  "option-count": "Disaggregation",
  "missingness-count": "Recorded",
  "status-count": "Completion",
  "value-sum": "Sum",
  "value-average": "Average"
};

// Tally has two axes: METHOD (what record set is scanned) and KIND (how
// matches are combined). Method drives the *base number* — count of
// events, enrollments, TEIs, aggregate facts, or numeric DE/TEA values.
// Kind drives the operation — count, sum, average, etc.
const METHOD_LABELS: Record<NonNullable<Tally["method"]>, string> = {
  "event":           "Event count",
  "enrollment":      "Enrollment count",
  "tracked-entity":  "TEI count",
  "data-value":      "Data-value op",
  "attribute-value": "Attribute-value op",
  "aggregate-fact":  "Aggregate fact"
};

const METHOD_FILTER_LABELS: Record<string, string> = {
  ...METHOD_LABELS,
  "all": "All methods"
};

function methodOf(t: Tally): NonNullable<Tally["method"]> {
  if (t.method) return t.method;
  // Mirror inferTrackerTallyMethod for legacy rows without a stored
  // method — keeps the UI consistent before/after the next regeneration.
  if (t.id?.includes(":agg:")) return "aggregate-fact";
  if (t.kind === "value-sum" || t.kind === "value-average" || t.kind === "value-min" || t.kind === "value-max") {
    return t.attributeDimensionId && !t.dataElementId ? "attribute-value" : "data-value";
  }
  if (t.attributeDimensionId && !t.dataElementId) return "enrollment";
  return "event";
}

function displayMethod(t: Tally): string {
  return METHOD_LABELS[methodOf(t)];
}

function displayKind(t: Tally): string {
  if (t.notAggregable) return "N/A";
  switch (t.kind) {
    case "value-sum": return "Sum";
    case "value-average": return "Average";
    case "value-min": return "Min";
    case "value-max": return "Max";
    case "count-unique": return "Count unique";
    case "missingness-count": return "Recorded";
    case "status-count": return "Completion";
    case "option-count":
      if (t.optionValue) return "Disaggregated count";
      return "Total count";
    case "event-count":
    default:
      return "Total count";
  }
}

export interface TallyMetricsProps {
  scope: "workspace" | "platform";
  /**
   * "catalog" presents the page as a metadata view — the auto-generated
   * variable list is the focus, source/aggregation columns are
   * prominent, and the bucket-count columns are hidden. "values" (default)
   * keeps the operational view that surfaces actual tallied counts.
   * Routed twice: /admin/metadata/tally-variables (catalog) and
   * /admin/data/tallymetrics (values).
   */
  view?: "catalog" | "values";
}

export default function TallyMetrics({ scope, view = "values" }: TallyMetricsProps) {
  const isCatalog = view === "catalog";
  const { orgUnitName, programName, dataElementName, dataSetName, programStageName } = useLabels();
  const [tallies, setTallies] = useState<Tally[] | null>(null);
  // Synced-data preview surfaces counts + sample events/TEIs/aggregate
  // facts for the workspace so analysts can see what's actually been
  // received before the tally engine has had a chance to bucket it.
  // Only meaningful on the data view (catalogue is structural).
  type DataPreview = Awaited<ReturnType<typeof api.dataPreview>>;
  const [dataPreview, setDataPreview] = useState<DataPreview | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  // Catalog size reported by the server; may exceed tallies.length when
  // the response is capped (server caps at 5000). Used to honestly
  // surface "X loaded of Y total" so the user knows when to refine.
  const [serverTotalCount, setServerTotalCount] = useState<number | null>(null);
  const [buckets, setBuckets] = useState<Bucket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [methodFilter, setMethodFilter] = useState<string>("all");
  const [programFilter, setProgramFilter] = useState<string>("all");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  // Program-type filter: tracker / event-only / aggregate / all.
  const [programTypeFilter, setProgramTypeFilter] = useState<"all" | "WITH_REGISTRATION" | "WITHOUT_REGISTRATION" | "aggregate">("all");
  // Catalogue auditors usually want to see N/A rows alongside the
  // tallyable ones — they're part of the metadata census. Default ON
  // for catalogue, default OFF for the data view (where they'd just
  // dilute the active values list).
  const [includeNA, setIncludeNA] = useState<boolean>(true);
  // Default to hiding rows with no buckets — long catalogs of 0/0 are
  // noise. Toggle off to audit the full catalog (useful when checking
  // that auto-generation produced the expected variables before any
  // events have landed).
  // Default off: with thousands of auto-generated variables and a fresh
  // workspace whose first events haven't synced yet, defaulting to on
  // hides the entire catalog and the empty-state takes over. Operators
  // can turn it on once data is flowing to focus on active rows.
  const [hideZero, setHideZero] = useState(false);
  const [liveRefreshes, setLiveRefreshes] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);

  // Platform admins drill into a specific workspace via the
  // WorkspaceSelector dropdown, which writes ?workspaceId=… into the
  // URL. Workspace users always see their own workspace; the URL
  // param is ignored server-side for tenant tokens.
  const [searchParams] = useSearchParams();
  const platformWorkspaceFilter = scope === "platform" ? (searchParams.get("workspaceId") || undefined) : undefined;

  const refresh = () => {
    setLastRefreshAt(Date.now());
    const opts = platformWorkspaceFilter ? { workspaceId: platformWorkspaceFilter } : undefined;
    api.listTallies(opts).then((r) => {
      setTallies(r.tallies);
      setServerTotalCount(typeof r.totalCount === "number" ? r.totalCount : r.tallies.length);
    }).catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    api.listBuckets().then((r) => setBuckets(r.buckets)).catch(() => {/* buckets are best-effort */});
    // Refresh the data-preview alongside; useful for the empty-state
    // diagnostic (helps explain "why are buckets at zero?").
    api.dataPreview(platformWorkspaceFilter ? { workspaceId: platformWorkspaceFilter, sample: 10 } : { sample: 10 })
      .then(setDataPreview)
      .catch(() => {/* preview is best-effort */});
  };
  useEffect(refresh, [platformWorkspaceFilter]);

  // TallyMetrics is *tracker-derived only* — it counts tracker
  // registrations / enrollments / events. Subscribe to tally + metadata
  // + trk-data so tracker syncs and tally recomputes refresh the view.
  // We deliberately do NOT subscribe to agg-data: aggregate dataValueSets
  // are pre-aggregated upstream and never tallied here.
  const live = useLiveEvents({
    scope: scope === "platform" ? "platform" : undefined,
    categories: ["tally", "trk-data", "metadata"],
    tailMax: 50
  });
  const debouncedRefRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (live.events.length === 0) return;
    setLiveRefreshes((n) => n + 1);
    if (debouncedRefRef.current) clearTimeout(debouncedRefRef.current);
    debouncedRefRef.current = setTimeout(() => { refresh(); }, 1500);
    return () => { if (debouncedRefRef.current) clearTimeout(debouncedRefRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.events[0]?.id]);  // only depend on the newest event id

  const liveCounters = live.countersIn(5 * 60 * 1000);
  const liveTone = live.connectionState === "open" ? "ok" : live.connectionState === "connecting" ? "info" : "err";
  const liveLabel = live.connectionState === "open"
    ? `● Live · ${liveCounters.total} events / 5m`
    : live.connectionState === "connecting" ? "Connecting…" : "Offline";

  // Track previous bucket counts so the table can briefly highlight rows
  // whose values changed between live refreshes — gives a "things are
  // happening" cue without a full re-render flash.
  const prevCountsRef = useRef<Map<string, { rows: number; total: number }>>(new Map());
  const [pulsedIds, setPulsedIds] = useState<Set<string>>(new Set());
  const bucketCountByMetric = useMemo(() => {
    const map = new Map<string, { rows: number; total: number }>();
    for (const b of buckets ?? []) {
      const cur = map.get(b.metricId) ?? { rows: 0, total: 0 };
      cur.rows += 1;
      cur.total += b.value;
      map.set(b.metricId, cur);
    }
    return map;
  }, [buckets]);

  // Diff against the previous snapshot to find rows whose bucket count
  // moved; highlight those briefly. Skip on first render (when prev is
  // empty) so we don't paint EVERY row green at mount.
  useEffect(() => {
    if (!buckets) return;
    const prev = prevCountsRef.current;
    if (prev.size === 0) {
      prevCountsRef.current = bucketCountByMetric;
      return;
    }
    const changed = new Set<string>();
    for (const [k, v] of bucketCountByMetric) {
      const old = prev.get(k);
      if (!old || old.rows !== v.rows || old.total !== v.total) changed.add(k);
    }
    prevCountsRef.current = bucketCountByMetric;
    if (changed.size === 0) return;
    setPulsedIds(changed);
    const t = setTimeout(() => setPulsedIds(new Set()), 1800);
    return () => clearTimeout(t);
  }, [bucketCountByMetric, buckets]);

  // Workspace-scoped catalog often runs into the 100s/1000s once option-set
  // expansion lands (e.g. HIV result × Sex × Age band per stage). Search
  // matches across the human-readable variableName, raw label, all
  // dimension display names, and any UID the user might paste in.
  // Distinct option lists for the filter dropdowns — derived from the
  // currently-loaded tally rows so the analyst only sees options that
  // would actually match. Programs / stages / sources come from the
  // dimensions[] array (clean names, populated by enrichTallyForDisplay).
  const knownKinds = useMemo(() => {
    const set = new Set<string>();
    for (const t of tallies ?? []) set.add(t.kind);
    return [...set].sort();
  }, [tallies]);
  const knownPrograms = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tallies ?? []) {
      const dim = (t.dimensions ?? []).find((d) => d.kind === "program");
      if (dim) map.set(dim.id, dim.name);
      else if (t.programId) map.set(t.programId, t.programId);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tallies]);
  const knownStages = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tallies ?? []) {
      if (programFilter !== "all" && t.programId !== programFilter) continue;
      const dim = (t.dimensions ?? []).find((d) => d.kind === "stage");
      if (dim) map.set(dim.id, dim.name);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tallies, programFilter]);
  const knownSources = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tallies ?? []) {
      const id = t.serviceProfileEnvironment ?? t.dhis2InstanceUrl;
      if (!id) continue;
      const label = t.dhis2InstanceLabel ?? t.serviceProfileEnvironment ?? id;
      map.set(id, label);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tallies]);

  const filtered = useMemo(() => {
    let rows = tallies ?? [];
    if (kindFilter !== "all") rows = rows.filter((t) => t.kind === kindFilter);
    if (methodFilter !== "all") rows = rows.filter((t) => methodOf(t) === methodFilter);
    if (programFilter !== "all") rows = rows.filter((t) => t.programId === programFilter);
    if (stageFilter !== "all") rows = rows.filter((t) => t.stageId === stageFilter);
    if (programTypeFilter !== "all") {
      rows = rows.filter((t) => {
        const programDim = (t.dimensions ?? []).find((d) => d.kind === "program");
        return programDim?.programType === programTypeFilter;
      });
    }
    if (sourceFilter !== "all") {
      rows = rows.filter((t) =>
        t.serviceProfileEnvironment === sourceFilter
        || t.dhis2InstanceUrl === sourceFilter
      );
    }
    if (!includeNA) rows = rows.filter((t) => !t.notAggregable);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((t) => {
        const dimText = (t.dimensions ?? []).map((d) => d.name.toLowerCase()).join(" ");
        return (t.variableName ?? "").toLowerCase().includes(q)
          || t.label.toLowerCase().includes(q)
          || dimText.includes(q)
          || t.id.toLowerCase().includes(q)
          || t.programId.toLowerCase().includes(q)
          || (t.dataElementId ?? "").toLowerCase().includes(q);
      });
    }
    if (hideZero && !isCatalog) {
      rows = rows.filter((t) => {
        if (t.notAggregable) return true;
        const c = bucketCountByMetric.get(t.id);
        return c !== undefined && c.rows > 0;
      });
    }
    return rows;
  }, [tallies, kindFilter, methodFilter, programFilter, stageFilter, sourceFilter, programTypeFilter, includeNA, search, hideZero, bucketCountByMetric, isCatalog]);

  // Pagination — client-side because /api/tallies returns the whole
  // catalog in-memory anyway, and the table's response time is bounded
  // by render. Resets to page 1 whenever the filtered set changes so the
  // user never lands on an empty page after typing.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [search, kindFilter, methodFilter, programFilter, stageFilter, sourceFilter, programTypeFilter, includeNA, pageSize]);
  // Stage filter is bounded by the program filter — when the program
  // changes, drop the stage filter unless it still applies.
  useEffect(() => {
    if (stageFilter !== "all" && !knownStages.some(([id]) => id === stageFilter)) {
      setStageFilter("all");
    }
  }, [programFilter, stageFilter, knownStages]);
  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  // Detail drawer — opens when a row is clicked; carries everything
  // (UIDs, dimensions, bucket samples) so the analyst can reconcile a
  // Metric Variable against DHIS2 directly.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => (selectedId ? (tallies ?? []).find((t) => t.id === selectedId) ?? null : null),
    [selectedId, tallies]
  );
  const selectedBuckets = useMemo(
    () => (selected ? (buckets ?? []).filter((b) => b.metricId === selected.id) : []),
    [selected, buckets]
  );

  async function onRecompute() {
    setRecomputing(true);
    setNotice(null);
    setError(null);
    try {
      const job = await api.recomputeTallies();
      setNotice(`Recompute job queued (${job.job.id}). Buckets refresh shortly — reload the page in a few seconds.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRecomputing(false);
    }
  }

  // Platform-admin "Regenerate all" — sweeps every configured workspace
  // through tracker-metadata-sync, which deletes stale auto-generated
  // rows and emits the catalog with the latest schema. Useful after a
  // schema change in the generator (e.g. switching the variable-naming
  // convention) so prior generations don't linger across tenants.
  const [regenerating, setRegenerating] = useState(false);
  async function onRegenerateAll() {
    if (!confirm(
      "Enqueue tracker-metadata-sync for every workspace whose DHIS2 instance has stored credentials? "
      + "This will replace stale auto-generated tallies platform-wide. The catalog repopulates as each sync completes."
    )) return;
    setRegenerating(true);
    setNotice(null);
    setError(null);
    try {
      const r = await api.regenerateAllTallies();
      setNotice(
        r.enqueued === 0
          ? "No configured workspaces to regenerate. Pair + store credentials on at least one DHIS2 instance first."
          : `Regenerate sweep queued — ${r.enqueued} tracker-metadata-sync job${r.enqueued === 1 ? "" : "s"} enqueued. Reload in a minute to see the refreshed catalogue.`
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>
            {isCatalog ? "TallyMetric variables" : "TallyMetrics"}
            <span className={`pill pill-${liveTone}`} style={{ fontSize: 11, marginLeft: 12, verticalAlign: "middle" }} title={`Connection: ${live.connectionState}${lastRefreshAt ? ` · last refresh ${Math.floor((Date.now() - lastRefreshAt) / 1000)}s ago` : ""} · ${liveRefreshes} live refreshes since open`}>
              {liveLabel}
            </span>
          </h1>
          <p className="muted">
            {isCatalog ? (
              <>
                Auto-generated metadata catalogue: every Metric Variable derived from synced
                tracker metadata. This is the structural map — what <em>can</em> be tallied.
                For actual counts and time series jump to{" "}
                <a href={scope === "platform" ? "/admin/data/tallymetrics" : "/app/tallying/tallymetrics"}>TallyMetrics (data)</a>.
              </>
            ) : (
              <>
                Live tallies — actual counts and totals derived by running tracker events through
                the auto-generated variable catalogue. For the variable definitions themselves,
                see{" "}
                <a href={scope === "platform" ? "/admin/metadata/tally-variables" : "/app/metadata/tally-variables"}>TallyMetric variables (metadata)</a>.
              </>
            )}
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
          {scope === "platform" && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void onRegenerateAll()}
              disabled={regenerating}
              title="Replace stale auto-generated tallies across every configured workspace"
            >
              {regenerating ? "Regenerating…" : "Regenerate all"}
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={onRecompute} disabled={recomputing}>
            {recomputing ? "Recomputing…" : "Recompute tallies"}
          </button>
        </div>
      </header>

      <DataFlowIndicator
        currentStage={isCatalog ? "definitions" : "buckets"}
        scope={scope}
      />

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {notice && <div className="ts-alert ts-alert-info">{notice}</div>}

      <section className="transfer-filters" aria-label="TallyMetric filters" style={{ flexWrap: "wrap" }}>
        {scope === "platform" && (
          <span className="transfer-filter" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <WorkspaceSelector configuredOnly label="Workspace" />
          </span>
        )}
        <label className="transfer-filter">
          Method
          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
            {Object.entries(METHOD_FILTER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Kind
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="all">All kinds</option>
            {knownKinds.map((k) => <option key={k} value={k}>{KIND_FILTER_LABELS[k] ?? k}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Program
          <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
            <option value="all">All programs ({knownPrograms.length})</option>
            {knownPrograms.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Stage
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
            <option value="all">{programFilter === "all" ? `All stages` : `All stages (${knownStages.length})`}</option>
            {knownStages.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label className="transfer-filter">
          Program type
          <select value={programTypeFilter} onChange={(e) => setProgramTypeFilter(e.target.value as typeof programTypeFilter)}>
            <option value="all">All program types</option>
            <option value="WITH_REGISTRATION">Tracker (registration)</option>
            <option value="WITHOUT_REGISTRATION">Event-only</option>
            <option value="aggregate">Aggregate (data sets)</option>
          </select>
        </label>
        <label className="transfer-filter">
          Source DHIS2
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="all">All sources ({knownSources.length})</option>
            {knownSources.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <label className="transfer-filter" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={includeNA} onChange={(e) => setIncludeNA(e.target.checked)} />
          Show N/A rows
        </label>
        {!isCatalog && (
          <label className="transfer-filter" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
            Hide zero-total rows
          </label>
        )}
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="label / id / program / DE / option…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        {(programFilter !== "all" || stageFilter !== "all" || sourceFilter !== "all" || programTypeFilter !== "all" || kindFilter !== "all" || !includeNA || search.trim()) && (
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setKindFilter("all");
              setProgramFilter("all");
              setStageFilter("all");
              setSourceFilter("all");
              setProgramTypeFilter("all");
              setIncludeNA(true);
              setSearch("");
            }}
          >
            ✕ Clear filters
          </button>
        )}
      </section>

      {/* Operational signals belong on the data/values view, not the
          metadata catalogue. The catalogue is the structural list of
          variables and shouldn't be busy with sync-progress chatter. */}
      {!isCatalog && (
        <LivePanel
          title="Live tally activity"
          categories={["tally", "trk-data", "metadata"]}
          tailMax={20}
        />
      )}

      {!isCatalog && dataPreview && (
        <section className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14 }}>
              Synced data received from DHIS2{" "}
              <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
                tracker events: <strong>{dataPreview.counts.trackerEvents.toLocaleString()}</strong>
                {" · "}TEIs: <strong>{dataPreview.counts.trackedEntities.toLocaleString()}</strong>
                {" · "}aggregate facts: <strong>{dataPreview.counts.submittedAggregateFacts.toLocaleString()}</strong>
              </span>
            </h2>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? "Hide details" : "Show samples"}
            </button>
          </div>
          {(() => {
            const counts = dataPreview.counts;
            const totalCount = counts.trackerEvents + counts.trackedEntities + counts.submittedAggregateFacts;
            if (totalCount > 0) {
              return (
                <div style={{ fontSize: 12, color: "#065f46", background: "#d1fae5", padding: 8, borderRadius: 6 }}>
                  Data is flowing. {counts.trackerEvents.toLocaleString()} tracker events
                  {" "}+ {counts.submittedAggregateFacts.toLocaleString()} aggregate facts received;
                  the tally engine buckets each one through every matching variable on ingest.
                </div>
              );
            }
            const trk = dataPreview.lastSync?.trackerEvents;
            const agg = dataPreview.lastSync?.submittedAggregateFacts;
            const anyRun = (trk?.totalRuns ?? 0) + (agg?.totalRuns ?? 0) > 0;
            const anyRunning = trk?.running || agg?.running;
            const lastFailedAt = [trk?.lastFailedAt, agg?.lastFailedAt].filter(Boolean).sort().slice(-1)[0] ?? null;
            const lastCompletedAt = [trk?.lastCompletedAt, agg?.lastCompletedAt].filter(Boolean).sort().slice(-1)[0] ?? null;
            const lastError = (trk?.lastFailedAt && trk?.lastError) || (agg?.lastFailedAt && agg?.lastError) || null;
            const failingNow =
              !!lastFailedAt && (!lastCompletedAt || Date.parse(lastFailedAt) > Date.parse(lastCompletedAt));
            const fmt = (s: string) => new Date(s).toLocaleString();

            if (!anyRun) {
              return (
                <div style={{ fontSize: 12, color: "#1e3a8a", background: "#dbeafe", padding: 8, borderRadius: 6 }}>
                  No sync has run yet for this workspace. Pair a DHIS2 instance and run
                  {" "}<strong>tracker-metadata-sync</strong> first, then a
                  {" "}<strong>tracker-events-sync</strong> (and/or
                  {" "}<strong>submitted-aggregate-sync</strong>) to populate buckets.
                </div>
              );
            }
            if (anyRunning) {
              return (
                <div style={{ fontSize: 12, color: "#1e3a8a", background: "#dbeafe", padding: 8, borderRadius: 6 }}>
                  A sync is in progress. Counts will appear here once it completes — see
                  {" "}<a href="/admin/jobs">/admin/jobs</a> for live status.
                </div>
              );
            }
            if (failingNow) {
              return (
                <div style={{ fontSize: 12, color: "#7f1d1d", background: "#fee2e2", padding: 8, borderRadius: 6 }}>
                  Last sync attempt failed{lastFailedAt ? ` at ${fmt(lastFailedAt)}` : ""}.
                  {lastError ? <> Error: <code>{lastError.slice(0, 240)}</code></> : null}
                  {" "}Check <a href="/admin/jobs">/admin/jobs</a> — typically credential, permission, or
                  unsupported-program issues.
                </div>
              );
            }
            return (
              <div style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", padding: 8, borderRadius: 6 }}>
                Last sync succeeded{lastCompletedAt ? ` at ${fmt(lastCompletedAt)}` : ""} but returned no records.
                Verify the paired DHIS2 instance has tracker programs / aggregate datasets you've subscribed to,
                that the credentials have read access, and that the tracker metadata snapshot is current.
              </div>
            );
          })()}
          {showPreview && (
            <div style={{ marginTop: 12, display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
              <div>
                <h3 style={{ fontSize: 12, marginBottom: 4 }}>Events by program ({dataPreview.eventsByProgram.length})</h3>
                {dataPreview.eventsByProgram.length === 0 ? (
                  <div className="muted" style={{ fontSize: 11 }}>No events.</div>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 11 }}>
                    {dataPreview.eventsByProgram.slice(0, 10).map((row) => (
                      <li key={row.programId} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <code style={{ color: "#475569" }}>{row.programId}</code>
                        <strong>{row.count.toLocaleString()}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 style={{ fontSize: 12, marginBottom: 4 }}>Aggregate facts by data set ({dataPreview.factsByDataSet.length})</h3>
                {dataPreview.factsByDataSet.length === 0 ? (
                  <div className="muted" style={{ fontSize: 11 }}>No aggregate facts.</div>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 11 }}>
                    {dataPreview.factsByDataSet.slice(0, 10).map((row) => (
                      <li key={row.dataSetId} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <code style={{ color: "#475569" }}>{row.dataSetId}</code>
                        <strong>{row.count.toLocaleString()}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {dataPreview.sample.trackerEvents.length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <h3 style={{ fontSize: 12, marginBottom: 4 }}>Recent tracker events ({dataPreview.sample.trackerEvents.length})</h3>
                  <table className="data-table" style={{ fontSize: 11 }}>
                    <thead><tr><th>Event</th><th>Program/Stage</th><th>Org unit</th><th>Date</th><th>Status</th><th style={{ textAlign: "right" }}>DVs</th></tr></thead>
                    <tbody>
                      {dataPreview.sample.trackerEvents.map((e) => (
                        <tr key={e.id}>
                          <td><code style={{ fontSize: 10 }}>{e.id.slice(0, 11)}…</code></td>
                          <td><NamedId id={e.programId} name={programName(e.programId)} /> / <NamedId id={e.programStageId} name={programStageName(e.programStageId)} /></td>
                          <td><NamedId id={e.orgUnitId} name={orgUnitName(e.orgUnitId)} /></td>
                          <td>{e.eventDate?.slice(0, 10)}</td>
                          <td>{e.status}</td>
                          <td style={{ textAlign: "right" }}>{e.dataValueCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {dataPreview.sample.submittedAggregateFacts.length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <h3 style={{ fontSize: 12, marginBottom: 4 }}>Recent aggregate facts ({dataPreview.sample.submittedAggregateFacts.length})</h3>
                  <table className="data-table" style={{ fontSize: 11 }}>
                    <thead><tr><th>DE</th><th>Data set</th><th>Period</th><th>Org unit</th><th style={{ textAlign: "right" }}>Value</th></tr></thead>
                    <tbody>
                      {dataPreview.sample.submittedAggregateFacts.map((f) => (
                        <tr key={f.id}>
                          <td><NamedId id={f.dataElementId} name={dataElementName(f.dataElementId)} /></td>
                          <td>{f.dataSetId ? <NamedId id={f.dataSetId} name={dataSetName(f.dataSetId)} /> : <span className="muted">—</span>}</td>
                          <td>{f.periodId}</td>
                          <td><NamedId id={f.orgUnitId} name={orgUnitName(f.orgUnitId)} /></td>
                          <td style={{ textAlign: "right" }}>{f.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {tallies === null && <SkeletonTable rows={6} columns={scope === "platform" ? 6 : 5} />}
      {tallies !== null && tallies.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No TallyMetrics generated yet</h2>
          <p>
            TallyMetrics are auto-derived from synced tracker metadata. To populate this catalog:
          </p>
          <ol>
            <li>Pair this workspace with a DHIS2 instance.</li>
            <li>Run a tracker metadata sync (auto-mode does this on its own).</li>
            <li>Once events land, hit <strong>Recompute tallies</strong> above.</li>
          </ol>
        </div>
      )}
      {tallies !== null && tallies.length > 0 && filtered.length === 0 && (
        <div className="empty-state-card" style={{ borderLeft: "4px solid #94a3b8" }}>
          <h3 style={{ marginTop: 0 }}>No rows match the current view.</h3>
          <p>
            The catalog has <strong>{tallies.length.toLocaleString()}</strong> Metric Variable{tallies.length === 1 ? "" : "s"}
            {hideZero ? <>, but every visible variable has zero buckets and <em>Hide zero-total rows</em> is on.</> : <> but none match your filters.</>}
          </p>
          <p>
            {hideZero
              ? <>Run a <strong>tracker-events-sync</strong> from /admin/jobs (or wait for the next scheduled tick) so events land and buckets fill, or untick <em>Hide zero-total rows</em> to audit the full catalog.</>
              : <>Adjust the Kind filter or clear the search to widen the view.</>}
          </p>
        </div>
      )}
      {filtered.length > 0 && (
        <>
          <div className="muted" style={{ marginBottom: 8, fontSize: 12 }}>
            {totalRows.toLocaleString()} Metric Variable{totalRows === 1 ? "" : "s"}
            {(tallies?.length ?? 0) !== totalRows ? ` (filtered from ${(tallies?.length ?? 0).toLocaleString()} loaded)` : ""}
            {serverTotalCount !== null && serverTotalCount > (tallies?.length ?? 0)
              ? ` · ${serverTotalCount.toLocaleString()} total in catalog (showing first ${(tallies?.length ?? 0).toLocaleString()})`
              : ""}
            {" · page "}{safePage}/{totalPages}
          </div>
          <table className="transfer-table">
            <thead>
              <tr>
                <th>Variable</th>
                <th>Method · Kind</th>
                <th>Dimensions</th>
                <th>Source</th>
                {scope === "platform" && <th>Workspace</th>}
                {!isCatalog && <th style={{ textAlign: "right" }}>Buckets</th>}
                {!isCatalog && <th style={{ textAlign: "right" }}>Total</th>}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((t) => {
                const counts = bucketCountByMetric.get(t.id);
                const pulsed = pulsedIds.has(t.id);
                const displayName = t.variableName ?? t.label;
                const dimChips = t.dimensions ?? [];
                return (
                  <tr
                    key={t.id}
                    className={pulsed ? "row-pulsed" : undefined}
                    onClick={() => setSelectedId(t.id)}
                    style={{ cursor: "pointer" }}
                    title="Open details"
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>{displayName}</div>
                      {t.provenance === "auto-generated" && (
                        <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>
                          auto-generated{t.metadataVersion ? ` · v${t.metadataVersion}` : ""}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span className="pill pill-info" style={{ fontSize: 10 }}>{displayMethod(t)}</span>
                        <span className="pill pill-muted" style={{ fontSize: 10 }}>{displayKind(t)}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {dimChips.length === 0 && <span className="muted" style={{ fontSize: 11 }}>—</span>}
                        {dimChips.map((d, i) => {
                          const programTypeBadge = d.kind === "program" && d.programType
                            ? d.programType === "WITHOUT_REGISTRATION"
                              ? " · Event-only"
                              : d.programType === "aggregate"
                                ? " · Aggregate"
                                : " · Tracker"
                            : "";
                          return (
                            <span
                              key={`${d.kind}-${d.id}-${i}`}
                              className={`pill pill-${d.kind === "option" || d.kind === "attribute-option" ? "info" : "muted"}`}
                              style={{ fontSize: 10 }}
                              title={`${d.kind}${programTypeBadge} · ${d.id}${d.optionSetName ? ` · ${d.optionSetName}` : ""}`}
                            >
                              {d.kind === "option" || d.kind === "attribute-option" ? "= " : ""}{d.name}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {t.dhis2InstanceLabel || t.serviceProfileEnvironment ? (
                        <span
                          className="pill pill-muted"
                          style={{ fontSize: 10 }}
                          title={t.dhis2InstanceUrl ?? t.serviceProfileEnvironment ?? ""}
                        >
                          {t.dhis2InstanceLabel ?? t.serviceProfileEnvironment}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    {scope === "platform" && <td className="mono" style={{ fontSize: 11 }}>{t.workspaceId ?? "—"}</td>}
                    {!isCatalog && (
                      <td
                        style={{ textAlign: "right", color: t.notAggregable ? "#9ca3af" : undefined }}
                        title={t.notAggregable ? (t.notAggregableReason ?? "Not aggregable per DHIS2 metadata.") : undefined}
                      >
                        {t.notAggregable ? "N/A" : (counts?.rows ?? 0)}
                      </td>
                    )}
                    {!isCatalog && (
                      <td
                        style={{ textAlign: "right", color: t.notAggregable ? "#9ca3af" : undefined }}
                        title={t.notAggregable ? (t.notAggregableReason ?? "Not aggregable per DHIS2 metadata.") : undefined}
                      >
                        {t.notAggregable ? "N/A" : (counts ? Math.round(counts.total) : 0)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="ts-pagination" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, fontSize: 12 }}>
            <label>
              Page size{" "}
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button type="button" className="btn btn-secondary" disabled={safePage <= 1} onClick={() => setPage(1)}>« First</button>
            <button type="button" className="btn btn-secondary" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>‹ Prev</button>
            <span className="muted">Page {safePage} of {totalPages}</span>
            <button type="button" className="btn btn-secondary" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next ›</button>
            <button type="button" className="btn btn-secondary" disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>Last »</button>
          </div>
        </>
      )}

      {selected && (
        <aside
          aria-label="Metric variable details"
          style={{
            position: "fixed", top: 0, right: 0, height: "100vh", width: "min(540px, 90vw)",
            background: "#fff", boxShadow: "-8px 0 24px rgba(15,23,42,0.18)",
            padding: "20px 24px", overflowY: "auto", zIndex: 50
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{selected.variableName ?? selected.label}</h2>
            <button type="button" className="btn btn-secondary" onClick={() => setSelectedId(null)} aria-label="Close details">✕</button>
          </div>
          <p className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            <strong>{displayMethod(selected)}</strong> · {displayKind(selected)}
            {selected.provenance ? ` · ${selected.provenance}` : ""}
            {selected.metadataVersion ? ` · v${selected.metadataVersion}` : ""}
          </p>
          {selected.notAggregable && (
            <div
              className="ts-alert ts-alert-info"
              style={{ marginTop: 12, fontSize: 12, background: "#fef3c7", color: "#78350f", padding: 8, borderRadius: 6 }}
            >
              <strong>Not aggregable.</strong> {selected.notAggregableReason ?? "DHIS2 metadata flagged this as not tally-able."}
            </div>
          )}

          {/* Bucket statistics — at-a-glance summary of the data this
              variable has produced so analysts don't have to skim the
              bucket table to know totals/coverage. */}
          <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>At a glance</h3>
          <BucketStats buckets={selectedBuckets} />

          {/* Names. Always show, even when label/variableName collapse to
              the same string — useful when comparing two variables that
              differ only by suffix/disaggregation. */}
          <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>Names</h3>
          <dl className="dl-grid" style={{ fontSize: 12 }}>
            <dt>Display label</dt><dd>{selected.variableName ?? selected.label}</dd>
            {selected.label && selected.label !== selected.variableName && <><dt>Generator label</dt><dd className="mono" style={{ fontSize: 11 }}>{selected.label}</dd></>}
            <dt>Method</dt><dd>{displayMethod(selected)} <code style={{ fontSize: 10, color: "#94a3b8" }}>({methodOf(selected)})</code></dd>
            <dt>Aggregation</dt><dd>{displayKind(selected)} <code style={{ fontSize: 10, color: "#94a3b8" }}>({selected.kind})</code></dd>
          </dl>

          <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>Dimensions</h3>
          {(selected.dimensions ?? []).length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No dimension breakdown for this variable.</div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {(selected.dimensions ?? []).map((d, i) => (
                <li key={`${d.kind}-${d.id}-${i}`} style={{ padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {d.kind}
                    {d.optionSetName ? ` · ${d.optionSetName}` : ""}
                    {d.programType ? ` · ${d.programType}` : ""}
                  </div>
                  <div>{d.name}</div>
                  <code style={{ fontSize: 11, color: "#94a3b8" }}>{d.id}</code>
                  {d.optionSetId && <span className="mono" style={{ fontSize: 10, color: "#94a3b8", marginLeft: 8 }}>option set: {d.optionSetId}</span>}
                </li>
              ))}
            </ul>
          )}

          {(selected.dhis2InstanceLabel || selected.dhis2InstanceUrl || selected.serviceProfileEnvironment) && (
            <>
              <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>Source DHIS2 instance</h3>
              <dl className="dl-grid" style={{ fontSize: 12 }}>
                {selected.dhis2InstanceLabel && <><dt>Label</dt><dd>{selected.dhis2InstanceLabel}</dd></>}
                {selected.serviceProfileEnvironment && <><dt>Environment</dt><dd className="mono">{selected.serviceProfileEnvironment}</dd></>}
                {selected.dhis2InstanceUrl && <><dt>Base URL</dt><dd className="mono" style={{ wordBreak: "break-all" }}>{selected.dhis2InstanceUrl}</dd></>}
              </dl>
            </>
          )}

          <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>Reference UIDs</h3>
          <dl className="dl-grid" style={{ fontSize: 12 }}>
            <dt>Tally / metric ID</dt><dd className="mono" style={{ wordBreak: "break-all" }}>{selected.id}</dd>
            <dt>Program</dt><dd className="mono">{selected.programId}</dd>
            {selected.stageId && <><dt>Stage</dt><dd className="mono">{selected.stageId}</dd></>}
            {selected.dataElementId && <><dt>Data element</dt><dd className="mono">{selected.dataElementId}</dd></>}
            {selected.optionValue && <><dt>Option value</dt><dd className="mono">{selected.optionValue}</dd></>}
            {selected.attributeDimensionId && <><dt>TEI attribute</dt><dd className="mono">{selected.attributeDimensionId}</dd></>}
            {selected.attributeOptionId && <><dt>TEI attribute option</dt><dd className="mono">{selected.attributeOptionId}</dd></>}
            {selected.workspaceId && <><dt>Workspace</dt><dd className="mono">{selected.workspaceId}</dd></>}
            {selected.metadataVersion !== undefined && <><dt>Metadata version</dt><dd>{selected.metadataVersion}</dd></>}
            {selected.provenance && <><dt>Provenance</dt><dd>{selected.provenance}</dd></>}
            {selected.notAggregable !== undefined && <><dt>Aggregable</dt><dd>{selected.notAggregable ? "no" : "yes"}</dd></>}
            {selected.notAggregableReason && <><dt>Aggregation block reason</dt><dd>{selected.notAggregableReason}</dd></>}
          </dl>

          {/* Raw definition — last resort for debugging when the curated
              fields above aren't enough (e.g. a curated tally with
              hand-edited bindings). Collapsed by default. */}
          <details style={{ marginTop: 20 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, color: "#64748b" }}>
              Raw definition (JSON)
            </summary>
            <pre
              style={{
                fontSize: 11, lineHeight: 1.4, marginTop: 8,
                background: "#0f172a", color: "#e2e8f0",
                padding: 12, borderRadius: 6, overflow: "auto", maxHeight: 320
              }}
            >
              {JSON.stringify(selected, null, 2)}
            </pre>
          </details>

          <h3 style={{ marginTop: 20, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Buckets ({selectedBuckets.length})
          </h3>
          {selectedBuckets.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>
              No buckets recorded yet. Run a tracker events sync, then <strong>Recompute tallies</strong>.
            </div>
          ) : (
            <BucketTable buckets={selectedBuckets.slice(0, 50)} totalCount={selectedBuckets.length} />
          )}
        </aside>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * BucketStats — compact summary of the bucket set for the selected
 * variable: total contributions, distinct periods + org units, plus the
 * effective metric ("sum of values" for value-* kinds, "count of buckets
 * with value > 0" for count-style kinds). Saves the analyst from
 * eye-balling 50 rows to know whether anything fired.
 * ----------------------------------------------------------------------- */
function BucketStats({ buckets }: { buckets: Bucket[] }) {
  if (buckets.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12 }}>
        No buckets recorded yet — run a tracker events sync, then{" "}
        <strong>Recompute tallies</strong>.
      </div>
    );
  }
  const total = buckets.reduce((sum, b) => sum + b.value, 0);
  const periods = new Set(buckets.map((b) => b.periodId));
  const orgUnits = new Set(buckets.map((b) => b.orgUnitId));
  const nonZero = buckets.filter((b) => b.value > 0).length;
  return (
    <dl className="dl-grid" style={{ fontSize: 12 }}>
      <dt>Total value</dt><dd>{Math.round(total).toLocaleString()}</dd>
      <dt>Buckets</dt><dd>{buckets.length.toLocaleString()} ({nonZero.toLocaleString()} non-zero)</dd>
      <dt>Distinct periods</dt><dd>{periods.size}</dd>
      <dt>Distinct org units</dt><dd>{orgUnits.size}</dd>
    </dl>
  );
}

/* -------------------------------------------------------------------------
 * Data-flow indicator — small inline pill row showing where this page sits
 * in the platform pipeline. Helps operators self-locate after the analysis
 * we did showed the synced/tallied/warehouse stages were easy to confuse.
 * ----------------------------------------------------------------------- */
type Stage = "synced" | "definitions" | "buckets" | "indicators" | "warehouse";

function DataFlowIndicator({
  currentStage, scope
}: { currentStage: Stage; scope: "workspace" | "platform" }) {
  const base = scope === "platform" ? "/admin" : "/app";
  const synced = scope === "platform" ? `${base}/sync/transfer-logs` : `${base}/data-activity/synced-data`;
  const definitions = scope === "platform" ? `${base}/metadata/tally-variables` : `${base}/metadata/tally-variables`;
  const buckets = scope === "platform" ? `${base}/data/tallymetrics` : `${base}/tallying/tallymetrics`;
  const indicators = scope === "platform" ? `${base}/data/indicators` : `${base}/tallying/indicators`;
  const warehouse = scope === "platform" ? `${base}/insights/warehouse` : `${base}/insights/warehouse`;
  const stages: Array<{ id: Stage; label: string; href: string }> = [
    { id: "synced", label: "Synced data", href: synced },
    { id: "definitions", label: "Tally variables", href: definitions },
    { id: "buckets", label: "Tally buckets", href: buckets },
    { id: "indicators", label: "Indicators", href: indicators },
    { id: "warehouse", label: "Warehouse", href: warehouse }
  ];
  return (
    <nav
      aria-label="Data flow"
      style={{
        display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        margin: "8px 0 16px", fontSize: 11
      }}
    >
      {stages.map((s, i) => {
        const active = s.id === currentStage;
        return (
          <Fragment key={s.id}>
            {i > 0 && <span style={{ color: "#94a3b8" }}>→</span>}
            <a
              href={s.href}
              style={{
                padding: "3px 10px",
                borderRadius: 12,
                border: active ? "1px solid #1e3a8a" : "1px solid #cbd5e1",
                background: active ? "#dbeafe" : "#fff",
                color: active ? "#1e3a8a" : "#475569",
                fontWeight: active ? 600 : 400,
                textDecoration: "none"
              }}
            >
              {s.label}
            </a>
          </Fragment>
        );
      })}
    </nav>
  );
}

/* -------------------------------------------------------------------------
 * Bucket table with inline contributor drill-down. Clicking a row toggles
 * the contributors panel for that bucket — closes the lineage gap between
 * "the bucket has value 7" and "here are the source records that built it".
 * Contributors are loaded lazily so the catalog list isn't slowed down by
 * an N+1 fetch when a variable has hundreds of buckets.
 * ----------------------------------------------------------------------- */
function BucketTable({ buckets, totalCount }: { buckets: Bucket[]; totalCount: number }) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const { orgUnitName } = useLabels();
  return (
    <table className="transfer-table" style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th>Period</th>
          <th>Org unit</th>
          <th style={{ textAlign: "right" }}>Value</th>
          <th style={{ width: 32 }} />
        </tr>
      </thead>
      <tbody>
        {buckets.map((b) => {
          const isOpen = expandedKey === b.key;
          return (
            <Fragment key={b.key}>
              <tr
                className="row-clickable"
                onClick={() => setExpandedKey(isOpen ? null : b.key)}
                aria-expanded={isOpen}
                title="Click to see the source records that contributed"
              >
                <td>{b.periodId}</td>
                <td><NamedId id={b.orgUnitId} name={orgUnitName(b.orgUnitId)} /></td>
                <td style={{ textAlign: "right" }}>{Math.round(b.value)}</td>
                <td style={{ textAlign: "right", color: "#64748b" }}>{isOpen ? "▾" : "▸"}</td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={4} style={{ background: "#f8fafc", padding: 8 }}>
                    <ContributorsPanel
                      metricId={b.metricId}
                      periodId={b.periodId}
                      orgUnitId={b.orgUnitId}
                      dimensionTuple={b.dimensionTuple ?? []}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
        {totalCount > buckets.length && (
          <tr><td colSpan={4} className="muted">… {totalCount - buckets.length} more</td></tr>
        )}
      </tbody>
    </table>
  );
}

type Contributors = Awaited<ReturnType<typeof api.bucketContributors>>["contributors"];

function ContributorsPanel({
  metricId, periodId, orgUnitId, dimensionTuple
}: { metricId: string; periodId: string; orgUnitId: string; dimensionTuple: string[] }) {
  const { orgUnitName, programName, programStageName, dataSetName } = useLabels();
  const [contributors, setContributors] = useState<Contributors | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Use the joined string as a stable scalar key — the parent passes a
  // fresh `b.dimensionTuple ?? []` on every render, and listing the array
  // itself in the deps would re-fire this effect on every parent render
  // (the previous fetch gets cancelled, a new one starts, and the
  // component is stuck in "Loading contributors…" forever).
  const tupleKey = (dimensionTuple ?? []).join("|");
  useEffect(() => {
    let cancelled = false;
    setContributors(null);
    setError(null);
    api.bucketContributors({
      metricId,
      periodId,
      orgUnitId,
      dimensionTuple: tupleKey ? tupleKey.split("|") : undefined
    })
      .then((r) => { if (!cancelled) setContributors(r.contributors); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [metricId, periodId, orgUnitId, tupleKey]);

  if (error) {
    return <div className="ts-alert ts-alert-error" style={{ fontSize: 11 }}>Failed to load contributors: {error}</div>;
  }
  if (contributors === null) {
    return <div className="muted" style={{ fontSize: 11 }}>Loading contributors…</div>;
  }
  if (contributors.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 11 }}>
        No contributing records found for this bucket. The bucket value may have been imported directly,
        or the contribution ledger hasn't been rebuilt since the last metadata change — try{" "}
        <strong>Recompute tallies</strong>.
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>
        {contributors.length} source record{contributors.length === 1 ? "" : "s"} contributed to this bucket
      </div>
      <table className="transfer-table" style={{ fontSize: 11 }}>
        <thead>
          <tr>
            <th>Source record</th>
            <th>Type</th>
            <th>Origin date</th>
            <th>Program / stage</th>
            <th>Org unit</th>
            <th>Status</th>
            <th style={{ textAlign: "right" }}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {contributors.slice(0, 100).map((c) => {
            const ev = c.trackerEvent;
            const fact = c.submittedAggregateFact;
            const totalDelta = c.contributions.reduce((s, x) => s + x.delta, 0);
            const kind = ev ? "tracker event" : fact ? "aggregate fact" : "source record";
            const date = ev?.eventDate ?? fact?.lastUpdated ?? c.contributions[0]?.metadataVersion;
            // Cell content: tracker events have program/stage/orgUnit/status,
            // aggregate facts have dataElement/dataSet/orgUnit; coerce both
            // into the same column shape for the operator's eye.
            const programLabelId = ev?.programId ?? fact?.dataSetId ?? "";
            const programLabelName = ev
              ? programName(ev.programId)
              : fact?.dataSetId
                ? dataSetName(fact.dataSetId)
                : "";
            const stageLabelId = ev?.programStageId ?? "";
            const stageLabelName = ev?.programStageId ? programStageName(ev.programStageId) : "";
            const orgUnitLabelId = ev?.orgUnitId ?? fact?.orgUnitId ?? "";
            const orgUnitLabelName = orgUnitLabelId ? orgUnitName(orgUnitLabelId) : "";
            return (
              <tr key={c.sourceRecordId}>
                <td className="mono" style={{ fontSize: 10 }}>{c.sourceRecordId}</td>
                <td>{kind}</td>
                <td>{date ? String(date).slice(0, 19).replace("T", " ") : "—"}</td>
                <td>
                  {programLabelId ? (
                    <>
                      <NamedId id={programLabelId} name={programLabelName} />
                      {stageLabelId && (
                        <>{" / "}<NamedId id={stageLabelId} name={stageLabelName} /></>
                      )}
                    </>
                  ) : <span className="muted">—</span>}
                </td>
                <td>
                  {orgUnitLabelId
                    ? <NamedId id={orgUnitLabelId} name={orgUnitLabelName} />
                    : <span className="muted">—</span>}
                </td>
                <td>{ev?.status ?? "—"}</td>
                <td style={{ textAlign: "right" }}>{totalDelta}</td>
              </tr>
            );
          })}
          {contributors.length > 100 && (
            <tr><td colSpan={7} className="muted">… {contributors.length - 100} more</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
